const INVENTORY_KEYS = [
  "inventory",
  "inventoryTotal",
  "totalInventory",
  "ticketInventory",
  "totaltickets",
  "totalTickets",
  "ticketCapacity",
  "totalTicketCapacity",
];

const AVAILABLE_KEYS = [
  "availableForSale",
  "availableforsale",
  "available_for_sale",
  "available",
  "ticketsAvailable",
  "remainingTickets",
  "ticketsRemaining",
];

const SOLD_TODAY_KEYS = [
  "ticketsSoldToday",
  "tickets_sold_today",
  "soldToday",
  "sold_today",
];

function numOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function unwrapMongoNumber(v) {
  if (v == null || v === "") return null;
  if (typeof v === "object" && v.$numberInt != null) return v.$numberInt;
  if (typeof v === "object" && v.$numberLong != null) return v.$numberLong;
  if (typeof v === "object" && v.$numberDouble != null) return v.$numberDouble;
  return v;
}

function pickScalar(row, keys) {
  if (!row || typeof row !== "object") return null;
  for (const key of keys) {
    const v = row[key];
    if (v != null && v !== "") return unwrapMongoNumber(v);
  }
  return null;
}

function isGuestlistTicketTier(tk) {
  if (!tk || typeof tk !== "object") return false;
  if (tk.isguestlist === true || tk.isGuestlist === true) return true;
  const label = String(tk.tickettype ?? tk.ticketType ?? tk.name ?? "")
    .trim()
    .toLowerCase();
  if (label.includes("guest")) return true;
  const price = unwrapMongoNumber(tk.ticketprice ?? tk.price);
  return price === 0;
}

/** Guestlist capacity / sold / remaining from nested ticket tiers or row-level API fields. */
export function pickGuestlistTicketStatsFromRow(row) {
  const tickets = Array.isArray(row?.tickets) ? row.tickets : [];
  let inventory = 0;
  let sold = 0;
  let available = 0;
  let fromTiers = false;

  for (const t of tickets) {
    if (!isGuestlistTicketTier(t)) continue;
    fromTiers = true;
    const total = numOrZero(
      unwrapMongoNumber(t?.totaltickets ?? t?.inventory ?? t?.totalInventory)
    );
    const tierSold = numOrZero(
      unwrapMongoNumber(t?.ticketssold ?? t?.ticketsSold ?? t?.sold)
    );
    inventory += total;
    sold += tierSold;
    const explicitAvail = pickScalar(t, AVAILABLE_KEYS);
    if (explicitAvail != null) {
      available += numOrZero(explicitAvail);
    } else {
      available += Math.max(0, total - tierSold);
    }
  }

  const rowGlSold = numOrZero(row?.guestlistTickets ?? row?.guestlist_tickets);
  const rowGlInv = numOrZero(
    row?.guestlistInventory ??
      row?.guestlist_inventory ??
      row?.guestlistCapacity ??
      row?.guestlist_capacity
  );

  if (fromTiers) {
    return {
      inventory,
      sold: rowGlSold > 0 ? rowGlSold : sold,
      available,
    };
  }

  return {
    inventory: rowGlInv,
    sold: rowGlSold,
    available: rowGlInv > 0 ? Math.max(0, rowGlInv - rowGlSold) : 0,
  };
}

/** Paid tickets only — subtract guestlist sold / capacity from event-level row fields. */
export function rowExcludingGuestlistTicketStats(row) {
  if (!row || typeof row !== "object") return row;

  const gl = pickGuestlistTicketStatsFromRow(row);
  if (gl.sold <= 0 && gl.inventory <= 0) return row;

  const out = { ...row };
  const sold = numOrZero(row.ticketsSold ?? row.ticketssold);
  const online = numOrZero(row.onlineTickets);
  const offline = numOrZero(row.offlineTickets);
  const paidSoldFromChannels = online + offline;

  if (paidSoldFromChannels > 0) {
    out.ticketsSold = paidSoldFromChannels;
    out.ticketssold = paidSoldFromChannels;
  } else if (sold > 0 || gl.sold > 0) {
    out.ticketsSold = Math.max(0, sold - gl.sold);
    out.ticketssold = out.ticketsSold;
  }

  const inv = pickScalar(row, INVENTORY_KEYS);
  if (inv != null && gl.inventory > 0) {
    const paidInv = Math.max(0, numOrZero(inv) - gl.inventory);
    out.inventory = paidInv;
    out.totalTickets = paidInv;
    out.totaltickets = paidInv;
  }

  const avail = pickScalar(row, AVAILABLE_KEYS);
  if (avail != null && (gl.available > 0 || gl.inventory > 0)) {
    const glAvail =
      gl.available > 0 ? gl.available : Math.max(0, gl.inventory - gl.sold);
    const paidAvail = Math.max(0, numOrZero(avail) - glAvail);
    out.availableForSale = paidAvail;
    out.available = paidAvail;
    out.availableforsale = paidAvail;
  }

  return out;
}

function buildStats({ inventory, available, ticketsSold, soldToday }) {
  const inv =
    inventory != null && inventory !== "" ? numOrZero(inventory) : null;
  const avail =
    available != null && available !== "" ? numOrZero(available) : null;

  let sold = null;
  if (inv != null && inv > 0 && avail != null) {
    sold = Math.max(0, inv - avail);
  } else if (ticketsSold != null && ticketsSold !== "") {
    sold = numOrZero(ticketsSold);
  }

  const total = inv != null && inv > 0 ? inv : null;
  let pct = null;
  if (sold != null && total != null && total > 0) {
    pct = Math.min(100, Math.round((sold / total) * 100));
  }

  let barLevel = null;
  if (pct != null) {
    if (pct >= 80) barLevel = "high";
    else if (pct >= 50) barLevel = "mid";
    else barLevel = "low";
  }

  const today =
    soldToday != null && soldToday !== "" ? numOrZero(soldToday) : null;

  return { sold, total, pct, barLevel, soldToday: today };
}

/** Per-row stats for event / category breakdown tables. */
export function deriveTicketSoldStats(row) {
  if (!row || typeof row !== "object") {
    return buildStats({});
  }
  return buildStats({
    inventory: pickScalar(row, INVENTORY_KEYS),
    available: pickScalar(row, AVAILABLE_KEYS),
    ticketsSold: row.ticketsSold ?? row.ticketssold,
    soldToday: pickScalar(row, SOLD_TODAY_KEYS),
  });
}

/** Aggregate total row (inventory / available / ticketsSold sums). */
export function deriveTicketSoldTotals(totals, { excludeGuestlist = false } = {}) {
  if (!totals || typeof totals !== "object") {
    return buildStats({});
  }

  let inventory = totals.inventory;
  let available = totals.available;
  let ticketsSold = totals.ticketsSold;

  if (excludeGuestlist) {
    const glSold = numOrZero(totals.guestlistTickets);
    const glInv = numOrZero(totals.guestlistInventory);
    const glAvail = numOrZero(totals.guestlistAvailable);
    ticketsSold = Math.max(0, numOrZero(ticketsSold) - glSold);
    if (glInv > 0) {
      inventory = Math.max(0, numOrZero(inventory) - glInv);
    }
    if (glAvail > 0) {
      available = Math.max(0, numOrZero(available) - glAvail);
    } else if (glInv > 0) {
      available = Math.max(0, numOrZero(available) - Math.max(0, glInv - glSold));
    }
  }

  return buildStats({
    inventory,
    available,
    ticketsSold,
    soldToday: totals.soldToday ?? totals.ticketsSoldToday,
  });
}
